import {i18n} from './I18n';
import dayjs, {Dayjs} from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const TIMEZONE = "Europe/Vienna";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(TIMEZONE);

export function now() {
	return dayjs().tz();
}

/**
 * Parse a Date or string without timezone information as Europe/Vienna
 *
 * @param date a datetime, e.g. 2024-07-01T12:00:00
 * @return a Dayjs instance, e.g. 2024-07-01T12:00:00+02:00
 */
export function asLocalTime(date: Date | string) {
	return date ? dayjs(date).tz(undefined, true) : null;
}

/**
 * Parse a Date, Dayjs object, UNIX timestamp, or string with timezone information and convert to Europe/Vienna
 *
 * @param date a datetime, e.g. 2024-07-01T12:00:00-04:00
 * @return a Dayjs instance, e.g. 2024-07-01T18:00:00+02:00
 */
export function asOffsetTime(date: Dayjs | Date | number | string) {
	return date ? dayjs(date).tz() : null;
}

export function atStartOfDay(date?: Dayjs): Dayjs {
    return (date ?? now()).startOf('day');
}

export function toDateSameLocalTime(date?: Dayjs) {
	return date ? date.tz(dayjs.tz.guess(), true).toDate() : null;
}

export function formatTime(date: Dayjs) {
    return date?.format('HH:mm');
}

export function formatDate(date: Dayjs, includeYear = true) {
    return date ? i18n.getMessage('date.fmt.dow', '', [
        // day of week
        getShortDow(date),
        // day of month
        date.format('DD'),
        // month
        getShortMonth(date),
        // year
        includeYear ? date.format('YYYY') : '',
    ]) : null;
}

export function formatNumericDate(date: Dayjs): string {
    return date ? i18n.getMessage('date.fmt.numeric', '', [
        // day of month
        date.format('DD'),
        // month
        date.format('MM'),
        // year
        date.format('YYYY'),
    ]) : null;
}

export function formatIsoDate(date: Dayjs): string {
    return date?.format('YYYY-MM-DD');
}

export function calculateDuration(fromDate?: Dayjs | number, toDate?: Dayjs | number): number {
    if (!fromDate || !toDate || fromDate > toDate) {
        return null;
    }

    return Math.round((+toDate - +fromDate) / (60 * 1000));
}

export function formatDuration(fromDate?: Dayjs | number, toDate?: Dayjs | number): string {
    if (!fromDate || !toDate || fromDate > toDate) {
        return null;
    }

    const duration = Math.round((+toDate - +fromDate) / (60 * 1000));
    const minutes = Math.floor(duration % 60);
    const hours = Math.floor(duration / 60);
    return hours > 0
        ? i18n.getMessage(`date.duration.hours`, '', [hours.toString(), minutes.toString()])
        : i18n.getMessage(`date.duration.minutes`, '', [minutes.toString()]);
}

export function getShortDow(date: Dayjs) {
	return date ? i18n.getMessage(`date.day.short.${days[date.day()]}`) : null;
}

export function getShortMonth(date: Dayjs) {
    return date ? i18n.getMessage(`date.month.short.${months[date.month()]}`) : null;
}

const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

export function getDateYearsAgo(years: number, date?: Dayjs) {
    return formatIsoDate((date ?? now()).subtract(years, 'years'));
}

export function getLaterDate<T extends Date | Dayjs | number>(a: T, b: T): T {
    if (!a && !b) {
        return null;
    }
    if (!a) {
        return b;
    }
    if (!b) {
        return a;
    }
    return a > b ? a : b;
}

export function formatToShortWithWeekday(date: Dayjs): string {
    return i18n.getMessage(`date.day.short.${days[date?.day()]}`) + ", " + date?.format("DD.MM.");
}
